<script>
import { defineComponent, ref, watch } from 'vue'
import { gsap } from 'gsap'

import TinPng from '@/assets/images/png/latta.png'

export default defineComponent({
  name: 'ThePreloader',
  setup () {
    const visible = ref(false)
    const entered = ref(false)
    const el = ref()
    const elBgRed = ref()
    const elBgBlue = ref()
    const elContent = ref()
    const squaresEl = ref([])
    const tinEl = ref()
    const tinShadow = ref()

    const timeline = gsap.timeline()
    const squaresTimeline = gsap.timeline({ repeat: -1 })
    const tinTimeline = gsap.timeline({ repeat: -1, yoyo: true })

    function enter () {
      squaresTimeline.fromTo(squaresEl.value, { autoAlpha: 0 }, { autoAlpha: 1, stagger: 0.5 })
      squaresTimeline.fromTo(squaresEl.value, { autoAlpha: 1 }, { autoAlpha: 0, stagger: 0.5 })
      tinTimeline.fromTo(tinEl.value, { y: '-8%' }, { y: '10%', duration: 1, ease: 'power1.inOut' })
      tinTimeline.fromTo(tinShadow.value, { autoAlpha: 0.5, scale: 0.85 }, { autoAlpha: 0.8, scale: 1.15, duration: 1, ease: 'power1.inOut' }, '<')
      timeline.fromTo(el.value, { autoAlpha: 0 }, { autoAlpha: 1 })
      timeline.fromTo(
        elBgBlue.value,
        { clipPath: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)' },
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', duration: 1.5, ease: 'power1.out' }
      )
      timeline.fromTo(
        elBgRed.value,
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)', duration: 1.5, ease: 'power3.out' },
        '<'
      )
      timeline.to(elContent.value, { autoAlpha: 1 }, '-=0.5')
      timeline.add(() => (entered.value = true))
    }

    function leave () {
      squaresTimeline.clear()
      tinTimeline.clear()
      timeline.to(elContent.value, { autoAlpha: 0 })
      timeline.to(
        elBgBlue.value,
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)', duration: 1.5, ease: 'power3.out' },
        '-=0.25'
      )
      timeline.fromTo(
        el.value,
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },
        { clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)', duration: 1.5, ease: 'power1.out' },
        '<'
      )
      timeline.set([el.value, elBgRed.value, elBgBlue.value, elContent.value], { clearProps: 'all' })
      timeline.set(el.value, { autoAlpha: 0 })
      timeline.add(() => (entered.value = false))
    }

    watch([visible, entered], ([newVisible, newEntered], [oldVisible, oldEntered]) => {
      if (newVisible && !oldVisible && !oldEntered) enter()
      if (!newVisible && newEntered) leave()
    })

    return {
      visible,
      el,
      elBgRed,
      elBgBlue,
      elContent,
      squaresEl,
      tinEl,
      tinShadow,
      TinPng
    }
  }
})
</script>

<template>
  <div id="app-loading" ref="el">
    <div ref="elBgRed" class="app-loading-background-red"></div>
    <div ref="elBgBlue" class="app-loading-background-blue"></div>
    <div ref="elContent" class="app-loading-content">
      <svg ref="elText" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 1260 217" xml:space="preserve" fill="currentColor" class="text">
        <path d="M319.786 34.9973v-30.6h-112.2v95.6787h49.994V63.5861h31.462V46.6339H257.58V34.9973h62.206ZM451.528 58.9889V4.3973h-44.391v51c0 11.924-3.304 17.0958-15.228 17.0958s-15.516-5.1718-15.516-17.0958v-51h-49.994V58.989c0 24.9971 17.814 44.6791 62.924 44.6791 45.253 0 62.205-19.682 62.205-44.6791ZM573.378 100.076V66.8903h-59.619v-8.1888h37.352V44.048h-37.352v-9.0507h59.619v-30.6H463.764v95.6787h109.614ZM693.909 100.076V66.8903h-60.338V4.3973h-49.995v95.6787h110.333ZM752.4 100.076V4.3973h-49.994v95.6787H752.4ZM890.097 100.076V4.3973h-44.966v37.0648l-3.305-3.5915-35.915-33.4733h-40.513v95.6787h44.823V62.7241l3.591 4.1662 33.474 33.1857h42.811ZM1025.28 100.076V48.2142h-59.616v16.9521h22.699v.5747c-3.161 4.7408-9.195 7.9014-16.665 7.9014-13.073 0-21.262-7.7578-21.262-18.8197v-.862c0-12.3549 10.056-21.262 25.859-21.262 11.206 0 20.831 2.7296 31.605 7.6141l15.09-27.8704C1008.33 5.2594 990.374.8058 966.526.8058c-38.358 0-67.09 20.9746-67.09 51v1.7239c0 30.4563 27.439 50.1383 60.913 50.1383 13.216 0 26.002-4.1665 34.909-12.2115l1.868 8.6195h28.154ZM120.819 153.727v-33.33H.1426v33.33h35.3408v62.349h49.9944v-62.349h35.3412ZM255.42 216.076v-95.679h-50.138v35.916h-25.285v-35.916h-49.994v95.679h49.994V182.89h25.285v33.186h50.138ZM377.948 216.076V182.89h-59.62v-8.188h37.352v-14.654h-37.352v-9.051h59.62v-30.6H268.334v95.679h109.614ZM549.905 168.955v-1.724c0-25.859-22.411-46.834-64.935-46.834h-61.487v95.679h61.487c42.524 0 64.935-21.405 64.935-47.121Zm-50.856.287c0 9.626-4.741 17.671-17.67 17.671h-7.902v-37.64h7.902c12.929 0 17.67 8.045 17.67 17.671v2.298ZM681.418 216.076v-1.436c-3.735-4.023-5.172-8.476-5.172-14.51v-7.902c0-11.062-4.597-20.831-21.262-22.411 14.079-2.73 22.842-12.211 22.842-21.406v-1.293c0-15.371-15.659-26.721-41.662-26.721h-76.859v95.679h49.851v-33.76h8.332c7.327 0 8.764 3.448 8.764 10.2v8.763c0 8.476 5.746 14.797 11.493 14.797h43.673ZM627.257 156.6c0 4.741-2.729 7.902-10.2 7.902h-7.901v-15.229h7.901c7.471 0 10.2 1.868 10.2 6.896v.431ZM797.85 216.076V182.89h-59.62v-8.188h37.353v-14.654H738.23v-9.051h59.62v-30.6H688.236v95.679H797.85Z" fill="#FFEB00" /><path d="m934.614 216.076-43.099-95.679h-51.431l-42.236 95.679h43.673l6.465-16.665h29.163l5.603 16.665h51.862Zm-64.648-38.501h-13.217l7.04-17.958 6.177 17.958ZM1106.91 216.076v-95.679h-68.1l-12.93 37.496-2.3 7.614-2.3-7.614-12.92-37.496h-70.399v95.679h42.524v-51.574l4.022 11.78 13.792 39.794h42.951l13.94-39.794 4.02-11.78v51.574h47.7Z" fill="currentColor" />
        <rect :ref="node => squaresEl.push(node)" x="1120" y="177" width="40" height="40" />
        <rect :ref="node => squaresEl.push(node)" x="1170" y="177" width="40" height="40" />
        <rect :ref="node => squaresEl.push(node)" x="1220" y="177" width="40" height="40" />
      </svg>
      <div class="tin-container">
        <img ref="tinEl" :src="TinPng" draggable="false" decoding="async">
        <div ref="tinShadow" class="tin-shadow"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
#app-loading {
  position: absolute;
  z-index: $z-preloader;
  top: 0;
  left: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-color: $c-white;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
  color: $c-yellow;
  opacity: 0;
  visibility: hidden;
}

.app-loading-background-red,
.app-loading-background-blue {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.app-loading-background-red {
  background-color: $c-red;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
}

.app-loading-background-blue {
  background-color: $c-blue;
  clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0% 100%);
}

.app-loading-content {
  opacity: 0;
  visibility: hidden;
}

.text {
  position: absolute;
  z-index: 2;
  top: 45%;
  left: 50%;
  width: 75vw;
  transform: translate(-50%, -45%);

  @screen beforeMd {
    width: 90vw;
  }
}

.tin-container {
  position: absolute;
  z-index: 2;
  top: 75%;
  left: 50%;
  width: rem(210px);
  height: rem(270px);
  transform: translate(-50%, -75%);

  @screen beforeMd {
    top: 60%;
    left: 50%;
    width: rem(150px);
    height: rem(200px);
    transform: translate(-50%, -60%);
  }

  img {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .tin-shadow {
    position: absolute;
    top: 115%;
    left: 40%;
    width: 65%;
    height: rem(16px);
    background-color: $c-black;
    border-radius: 50%;
    transform: translateX(-50%);

    @screen beforeMd {
      top: 115%;
    }
  }
}
</style>
